import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Crosshair, Target, MapPin, TrendingUp, AlertCircle, CheckCircle2, ArrowLeft, XCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrandAnalysis = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [step, setStep] = useState(1); // 1: analyzing, 2: results, 3: locations

  useEffect(() => {
    loadBrand();
  }, [brandId]);

  const loadBrand = async () => {
    try {
      const res = await axios.get(`${API}/brands/detail/${brandId}`);
      setBrand(res.data);
      
      if (res.data.brand_summary) {
        setAnalysis(res.data.brand_summary);
        setStep(2);
      } else {
        // Iniciar análise automaticamente
        setTimeout(() => analyzeBrand(), 1000);
      }
    } catch (error) {
      console.error("Erro ao carregar marca:", error);
      toast.error("Erro ao carregar dados da marca");
    }
  };

  const analyzeBrand = async () => {
    setAnalyzing(true);
    setStep(1);
    
    try {
      const res = await axios.post(`${API}/brands/${brandId}/analyze`);
      setAnalysis(res.data.analysis);
      setStep(2);
      toast.success("Análise concluída!");
    } catch (error) {
      console.error("Erro na análise:", error);
      toast.error("Erro ao analisar marca");
    } finally {
      setAnalyzing(false);
    }
  };

  const abortAnalysis = () => {
    setAnalyzing(false);
    toast.info("Análise cancelada");
    navigate(-1);
  };

  const recommendLocations = async () => {
    setStep(3);
    
    try {
      const res = await axios.get(`${API}/locations/recommend/${brandId}`);
      toast.success(`${res.data.places.length} locais recomendados!`);
      
      // Redirecionar para dashboard
      setTimeout(() => {
        navigate(`/dashboard/${brand.client_id}`);
      }, 2000);
    } catch (error) {
      console.error("Erro ao recomendar locais:", error);
      toast.error("Erro ao buscar locais");
    }
  };

  if (!brand) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="text-gray-400 mt-4">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-red-900/10 via-transparent to-transparent"></div>
      
      <div className="relative z-10 container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-gray-400 hover:text-white"
              data-testid="btn-back"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </div>
          <div className="flex items-center space-x-3">
            <Crosshair className="w-8 h-8 text-red-600" />
            <span className="text-2xl font-bold">ANÁLISE DE MARCA</span>
          </div>
          <div className="w-20"></div>
        </div>

        {/* Step 1: Analyzing */}
        {step === 1 && (
          <div className="max-w-3xl mx-auto" data-testid="analyzing-step">
            <Card className="bg-[#1a1a1a] border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl">Analisando sua marca...</CardTitle>
                <CardDescription>Nossa IA está processando todas as informações</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
                    <span>Identificando nicho e posicionamento...</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
                    <span>Analisando público real vs declarado...</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
                    <span>Avaliando tom de voz e estética...</span>
                  </div>
                </div>
                
                <Progress value={analyzing ? 60 : 100} className="h-2" />
                
                <div className="flex justify-center pt-4">
                  <Button
                    variant="outline"
                    onClick={abortAnalysis}
                    className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                    data-testid="btn-abort-analysis"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancelar Análise
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Results */}
        {step === 2 && analysis && (
          <div className="max-w-4xl mx-auto space-y-6" data-testid="results-step">
            <Card className="bg-[#1a1a1a] border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center">
                  <CheckCircle2 className="w-6 h-6 text-green-500 mr-2" />
                  Análise Concluída
                </CardTitle>
                <CardDescription>Veja o diagnóstico completo da sua marca</CardDescription>
              </CardHeader>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-[#1a1a1a] border-gray-800" data-testid="card-nicho">
                <CardHeader>
                  <CardTitle className="text-lg">Nicho</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xl font-semibold text-red-500">{analysis.nicho}</p>
                </CardContent>
              </Card>

              <Card className="bg-[#1a1a1a] border-gray-800" data-testid="card-classe">
                <CardHeader>
                  <CardTitle className="text-lg">Classe Social</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xl font-semibold text-red-500">{analysis.classe_social}</p>
                </CardContent>
              </Card>

              <Card className="bg-[#1a1a1a] border-gray-800" data-testid="card-preco">
                <CardHeader>
                  <CardTitle className="text-lg">Faixa de Preço</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xl font-semibold text-red-500">{analysis.faixa_preco}</p>
                </CardContent>
              </Card>

              <Card className="bg-[#1a1a1a] border-gray-800" data-testid="card-tom">
                <CardHeader>
                  <CardTitle className="text-lg">Tom de Voz</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xl font-semibold text-red-500">{analysis.tom_voz}</p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-[#1a1a1a] border-gray-800" data-testid="card-publico">
              <CardHeader>
                <CardTitle>Público Real</CardTitle>
                <CardDescription>Quem sua marca realmente atrai hoje</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300">{analysis.publico_real}</p>
              </CardContent>
            </Card>

            <Card className="bg-[#1a1a1a] border-gray-800" data-testid="card-diagnostico">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-yellow-500 mr-2" />
                  Diagnóstico
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300 mb-4">{analysis.diagnostico}</p>
                <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4">
                  <h4 className="font-semibold mb-2 text-red-500">O que corrigir:</h4>
                  <p className="text-gray-300">{analysis.o_que_corrigir}</p>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-center">
              <Button
                size="lg"
                onClick={recommendLocations}
                className="bg-red-600 hover:bg-red-700 px-8"
                data-testid="btn-find-locations"
              >
                <MapPin className="w-5 h-5 mr-2" />
                Encontrar Locais Ideais
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Finding Locations */}
        {step === 3 && (
          <div className="max-w-3xl mx-auto" data-testid="locations-step">
            <Card className="bg-[#1a1a1a] border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl">Buscando locais ideais...</CardTitle>
                <CardDescription>Identificando onde seu público circula</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
                    <span>Consultando API Foursquare...</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
                    <span>Criando geofences...</span>
                  </div>
                </div>
                
                <Progress value={75} className="h-2" />
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandAnalysis;
